import React, { useCallback, useEffect, useState } from 'react'

import ReactPlayer, {
  YouTubeConfig,
  YouTubePlayerProps,
} from 'react-player/youtube'
import YTPlayer from 'react-player/types/youtube'
import { BaseReactPlayerProps } from 'react-player/types/base'
import screenfull from 'screenfull'

import { isEqual } from 'lodash'
import { sleep } from 'shared/utils/sleep'
import { useWindowSize } from 'shared/hooks'

import classes from './YouTubePlayer.module.scss'
import classNames from 'classnames/bind'

export const YouTubePlayer = ({
  url: urls,
  loop,
  fullscreen = false,
  playing: isPlaying = false,
  controls = false,
  volume = 0,
  config,
  exitFullscreen = () => null,
  toggleMute = () => null,
  isActive = true,
  isNarrow = false,
  ...rest
}: YouTubePlayerProps): JSX.Element => {
  const playerRef = React.createRef<YTPlayer>()
  const wrapperRef = React.createRef<HTMLDivElement>()

  const [url, setUrl] = useState(Array.isArray(urls) ? urls[0] : urls)
  const [playing, setPlaying] = useState(isPlaying)
  const [hidden, setHidden] = useState(true)

  const onFullScreenChange = useCallback(() => {
    if (isActive && screenfull.isEnabled && !screenfull.isFullscreen) {
      if (!playing) {
        if (volume !== 0) toggleMute()
        setPlaying(true)
      }
      exitFullscreen()
    }
  }, [isActive, playing, exitFullscreen, volume, toggleMute])

  useEffect(() => {
    if (screenfull.isEnabled) {
      screenfull.onchange(onFullScreenChange)

      return () => {
        if (screenfull.isEnabled) {
          screenfull.off('change', onFullScreenChange)
        }
      }
    }
  }, [onFullScreenChange])

  useEffect(() => {
    if (wrapperRef.current && screenfull.isEnabled) {
      if (fullscreen) {
        screenfull.request(wrapperRef.current, { navigationUI: 'show' }).catch()
      } else {
        screenfull.exit().catch()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen])

  useEffect(() => {
    if (isPlaying) {
      playerRef?.current?.seekTo(0)
      setPlaying(true)
    } else {
      sleep(400).then(() => setPlaying(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying])

  // for looping video, default loop setting doesn't work properly for youtube
  const handleProgress = (progress: {
    played: number
    playedSeconds: number
    loaded: number
    loadedSeconds: number
  }) => {
    if (
      screenfull.isEnabled &&
      screenfull.isFullscreen &&
      progress?.played >= 0.91
    ) {
      exitFullscreen()
    }
    if (progress?.played >= 0.92) {
      onVideoAlmostEnd()
    }
  }

  const onVideoAlmostEnd = () => {
    if (Array.isArray(urls)) {
      const nextIdx = urls.findIndex(urlsEl => isEqual(urlsEl, url)) + 1
      const nextVideoIdx = nextIdx <= urls.length - 1 ? nextIdx : 0
      if (loop || (!loop && nextVideoIdx !== 0)) {
        setUrl(urls[nextVideoIdx])
        playerRef?.current?.seekTo(0)
        setPlaying(true)
      }
    } else if (loop) {
      playerRef?.current?.seekTo(0)
    }
  }

  const handleBufferEnd = () => {
    if (hidden) {
      setHidden(false)
    }
  }

  const youTubeConfig: YouTubeConfig = config || {
    playerVars: {
      enablejsapi: 1,
      showinfo: 0,
      rel: 0,
      fs: 1,
      disablekb: 1,
      controls: 0,
      autoplay: 0,
      // loop: 1,
      // modestbranding: 0,
      // origin: WEBSITE_URL,
    },
  }

  const [vertical, setVertical] = useState(false)
  const { height: windowHeight, width: windowWidth } = useWindowSize()

  useEffect(() => {
    if (isNarrow && wrapperRef.current) {
      const wrapperWidth = wrapperRef.current.offsetWidth
      const wrapperHeight = wrapperRef.current.offsetHeight
      setVertical(wrapperWidth / wrapperHeight < 1440 / 592)
    } else {
      setVertical(windowWidth / windowHeight < 1280 / 720)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowHeight, isNarrow, windowWidth])

  const wrapClass = classNames(classes.wrapper, {
    [classes.hidden]: hidden,
    [classes.vertical]: vertical,
    [classes.isNarrow]: isNarrow,
  })

  const urlWithParamFixForYouTube = `${url}?origin=${window.location.origin}`

  return (
    <div ref={wrapperRef} className={wrapClass}>
      <ReactPlayer
        onBufferEnd={() => handleBufferEnd()}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        ref={playerRef}
        url={urlWithParamFixForYouTube as BaseReactPlayerProps['url']}
        playing={playing}
        controls={controls}
        volume={volume}
        progressInterval={100}
        onProgress={progress => handleProgress(progress)}
        className={classes.player}
        height="100%"
        width="100%"
        config={youTubeConfig}
        {...rest}
      />
    </div>
  )
}
